import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, Button, Avatar, TextField, Checkbox } from '@mui/material';

// ============================================
// 전체 레이아웃
// ============================================
export const PageContainer = styled(Box)({
  display: 'flex',
  gap: '24px',
  padding: '24px 0',
  minHeight: 'calc(100vh - 200px)',
});

// ============================================
// 좌측 프로필 패널
// ============================================
export const ProfilePanel = styled(Box)({
  width: '320px',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
});

export const ProfileCard = styled(Box)({
  background: '#fff',
  borderRadius: '12px',
  border: '1px solid #f2f2f2',
  padding: '28px 24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
});

export const ProfileAvatar = styled(Avatar)({
  width: '100px',
  height: '100px',
  border: '3px solid #f0f0f0',
});

export const ProfileName = styled(Typography)({
  fontSize: '18px',
  fontWeight: 700,
  color: '#262626',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const ProfilePosition = styled(Typography)({
  fontSize: '14px',
  color: '#8c8c8c',
  marginTop: '2px',
});

export const SettlementBadge = styled(Box)(({ status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 600,
  ...(status === '정산중' && {
    backgroundColor: '#E6F7F8',
    color: '#0F7F85',
  }),
  ...(status === '정산완료' && {
    backgroundColor: '#F0F0F0',
    color: '#595959',
  }),
  ...(status === '미정산' && {
    backgroundColor: '#FFF1F0',
    color: '#CF1322',
  }),
}));

export const ProfileInfoTable = styled(Box)({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '0 4px',
});

export const ProfileInfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const ProfileInfoLabel = styled(Typography)({
  fontSize: '13px',
  color: '#8c8c8c',
  width: '65px',
  flexShrink: 0,
});

export const ProfileInfoValue = styled(Typography)({
  fontSize: '13px',
  color: '#262626',
  fontWeight: 500,
});

export const ProfileInfoHighlight = styled(Typography)(({ color }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: color || '#4AB3BC',
  cursor: 'default',
}));

// 신규/재등록 현황 카드
export const RegistrationCard = styled(Box)({
  background: '#fff',
  borderRadius: '12px',
  border: '1px solid #f2f2f2',
  padding: '20px 24px',
});

export const RegistrationTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#262626',
  marginBottom: '12px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const RegistrationRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 0',
});

export const RegistrationLabel = styled(Typography)({
  fontSize: '13px',
  color: '#8c8c8c',
});

export const RegistrationValue = styled(Typography)(({ color }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: color || '#262626',
}));

// ============================================
// 우측 메인 컨텐츠
// ============================================
export const ContentPanel = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  minWidth: 0,
});

// 스텝 탭
export const StepTabsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0',
});

export const StepTab = styled(Box)(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  borderRadius: '999px',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s',
  ...(active
    ? {
        backgroundColor: '#4AB3BC',
        color: '#fff',
      }
    : {
        backgroundColor: '#F5F5F5',
        color: '#8c8c8c',
      }),
}));

export const StepNumber = styled(Box)(({ active }) => ({
  width: '22px',
  height: '22px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 700,
  ...(active
    ? {
        backgroundColor: '#fff',
        color: '#4AB3BC',
      }
    : {
        backgroundColor: '#D9D9D9',
        color: '#fff',
      }),
}));

export const StepConnector = styled(Box)({
  width: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#D9D9D9',
  fontSize: '18px',
});

export const BackToListButton = styled(Button)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#595959',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#F5F5F5',
  },
});

// 요약 카드
export const SummaryCardsContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
});

export const SummaryCard = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px 20px',
  background: '#fff',
  borderRadius: '12px',
  border: '1px solid #f2f2f2',
});

export const SummaryIconBox = styled(Box)(({ bgColor }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: bgColor || '#E6F7F8',
  flexShrink: 0,
}));

export const SummaryLabel = styled(Typography)({
  fontSize: '13px',
  color: '#8c8c8c',
});

export const SummaryValue = styled(Typography)({
  fontSize: '18px',
  fontWeight: 700,
  color: '#262626',
});

// 개인레슨/그룹레슨 탭
export const LessonTabsContainer = styled(Box)({
  display: 'flex',
  borderBottom: '2px solid #f0f0f0',
});

export const LessonTab = styled(Box)(({ active }) => ({
  flex: 1,
  textAlign: 'center',
  padding: '12px 0',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s',
  ...(active
    ? {
        color: '#4AB3BC',
        borderBottom: '2px solid #4AB3BC',
        marginBottom: '-2px',
      }
    : {
        color: '#BFBFBF',
      }),
}));

// 테이블 영역
export const TableHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 0',
});

export const TableHeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const TableHeaderTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#262626',
});

export const PeriodText = styled(Typography)({
  fontSize: '13px',
  color: '#8c8c8c',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const TotalAmountBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  border: '1px solid #4AB3BC',
  borderRadius: '8px',
});

export const TotalAmountLabel = styled(Typography)({
  fontSize: '13px',
  color: '#8c8c8c',
});

export const TotalAmountValue = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#4AB3BC',
});

// 테이블 스타일
// 스크롤은 head+body를 함께 담은 이 컨테이너에서 처리한다 — body만 따로 스크롤하면
// 스크롤바 너비만큼 body 폭이 줄어서 head와 컬럼이 어긋난다.
export const StyledTable = styled(Box)({
  width: '100%',
  maxHeight: '480px',
  overflowY: 'auto',
  overflowX: 'hidden',
  borderRadius: '8px',
  border: '1px solid #f0f0f0',
});

export const StyledTableHead = styled(Box)({
  display: 'flex',
  backgroundColor: '#F0FAF9',
  borderBottom: '1px solid #E8E8E8',
  position: 'sticky',
  top: 0,
  zIndex: 1,
});

export const StyledTableHeadCell = styled(Box)(({ flex, minWidth }) => ({
  flex: flex || 1,
  minWidth: minWidth || 'auto',
  padding: '10px 12px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#595959',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const StyledTableBody = styled(Box)({});

export const StyledTableRow = styled(Box)(({ isEven }) => ({
  display: 'flex',
  borderBottom: '1px solid #F5F5F5',
  backgroundColor: isEven ? '#FAFAFA' : '#fff',
  '&:hover': {
    backgroundColor: '#F0FAF9',
  },
}));

export const StyledTableCell = styled(Box)(({ flex, minWidth, highlight }) => ({
  flex: flex || 1,
  minWidth: minWidth || 'auto',
  padding: '10px 12px',
  fontSize: '13px',
  color: highlight ? '#4AB3BC' : '#404040',
  fontWeight: highlight ? 600 : 400,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// 하단 버튼 영역
export const FooterBar = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  padding: '20px 0',
});

export const CancelButton = styled(Button)({
  minWidth: '80px',
  height: '44px',
  fontSize: '15px',
  fontWeight: 600,
  color: '#595959',
  backgroundColor: '#F5F5F5',
  borderRadius: '8px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#E8E8E8',
  },
});

export const NextButton = styled(Button)({
  minWidth: '120px',
  height: '44px',
  fontSize: '15px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: '#4AB3BC',
  borderRadius: '8px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#3A9DA5',
  },
});

export const PrevButton = styled(Button)({
  minWidth: '120px',
  height: '44px',
  fontSize: '15px',
  fontWeight: 600,
  color: '#4AB3BC',
  backgroundColor: '#fff',
  border: '1px solid #4AB3BC',
  borderRadius: '8px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#F0FAF9',
  },
});

// ============================================
// 검색 영역
// ============================================
export const SearchInput = styled(TextField)({
  width: '160px',
  '& .MuiOutlinedInput-root': {
    height: '34px',
    fontSize: '13px',
    borderRadius: '6px',
    '& fieldset': {
      borderColor: '#E8E8E8',
    },
  },
});

export const TealCheckbox = styled(Checkbox)({
  padding: '2px',
  color: '#D9D9D9',
  '&.Mui-checked': {
    color: '#4AB3BC',
  },
});

// ============================================
// Step 2: 급여 항목
// ============================================

// 상단 요약 카드 (지급 합계, 공제 합계, 최종 실지급액)
export const PaySummaryCard = styled(Box)(({ variant }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '20px 24px',
  borderRadius: '12px',
  ...(variant === 'pay' && {
    background: '#fff',
    border: '1px solid #f2f2f2',
  }),
  ...(variant === 'deduct' && {
    background: '#fff',
    border: '1px solid #f2f2f2',
  }),
  ...(variant === 'total' && {
    background: '#4AB3BC',
    border: 'none',
  }),
}));

export const PaySummaryIcon = styled(Box)(({ variant }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '18px',
  fontWeight: 700,
  ...(variant === 'pay' && {
    backgroundColor: '#E6F7F8',
    color: '#4AB3BC',
  }),
  ...(variant === 'deduct' && {
    backgroundColor: '#FFF1F0',
    color: '#CF1322',
  }),
  ...(variant === 'total' && {
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: '#fff',
  }),
}));

export const PaySummaryLabel = styled(Typography)(({ variant }) => ({
  fontSize: '13px',
  color: variant === 'total' ? 'rgba(255,255,255,0.8)' : '#8c8c8c',
}));

export const PaySummaryValue = styled(Typography)(({ variant }) => ({
  fontSize: '20px',
  fontWeight: 700,
  color: variant === 'total' ? '#fff' : '#262626',
}));

// 지급/공제 항목 레이아웃
export const PayItemsContainer = styled(Box)({
  display: 'flex',
  gap: '20px',
  flex: 1,
});

export const PayItemsColumn = styled(Box)(({ flex }) => ({
  flex: flex || 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
}));

export const PayItemsHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  borderRadius: '12px 12px 0 0',
  background: '#FAFAFA',
  borderBottom: '1px solid #f0f0f0',
});

export const PayItemsHeaderTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#262626',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

export const PayItemsHeaderAmount = styled(Typography)({
  fontSize: '18px',
  fontWeight: 700,
  color: '#262626',
});

export const PayItemCard = styled(Box)({
  padding: '16px 20px',
  background: '#fff',
  borderBottom: '1px solid #F5F5F5',
  '&:last-child': {
    borderBottom: 'none',
    borderRadius: '0 0 12px 12px',
  },
});

export const PayItemRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
});

export const PayItemTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
});

export const PayItemAmount = styled(Typography)(({ color }) => ({
  fontSize: '15px',
  fontWeight: 700,
  color: color || '#262626',
}));

export const AdjustButton = styled(Button)({
  minWidth: '68px',
  height: '28px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#4AB3BC',
  border: '1px solid #4AB3BC',
  borderRadius: '6px',
  padding: '0 10px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#F0FAF9',
  },
});

export const PayItemSubRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '2px 0 2px 12px',
});

export const PayItemSubLabel = styled(Typography)({
  fontSize: '13px',
  color: '#8c8c8c',
});

export const PayItemSubAmount = styled(Typography)({
  fontSize: '13px',
  color: '#595959',
});

export const PayItemsWrapper = styled(Box)({
  border: '1px solid #f0f0f0',
  borderRadius: '12px',
  overflow: 'hidden',
});

// ============================================
// 금액조정 모달
// ============================================
export const AdjustModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '480px',
  backgroundColor: '#fff',
  borderRadius: '16px',
  overflow: 'hidden',
  outline: 'none',
});

export const AdjustModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
  backgroundColor: '#333',
  color: '#fff',
});

export const AdjustModalTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#fff',
});

export const AdjustModalBody = styled(Box)({
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const AdjustTabsContainer = styled(Box)({
  display: 'flex',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid #E8E8E8',
});

export const AdjustTab = styled(Box)(({ active }) => ({
  flex: 1,
  textAlign: 'center',
  padding: '10px 0',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s',
  ...(active
    ? {
        backgroundColor: '#4AB3BC',
        color: '#fff',
      }
    : {
        backgroundColor: '#fff',
        color: '#8c8c8c',
      }),
}));

export const AdjustInfoCard = styled(Box)({
  border: '1px solid #f0f0f0',
  borderRadius: '12px',
  padding: '16px 20px',
});

export const AdjustInfoRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const AdjustInfoLabel = styled(Typography)({
  fontSize: '12px',
  color: '#8c8c8c',
});

export const AdjustInfoTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#262626',
});

export const AdjustInfoBadge = styled(Box)(({ type }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 600,
  ...(type === 'pay' && {
    backgroundColor: '#E6F7F8',
    color: '#4AB3BC',
  }),
  ...(type === 'deduct' && {
    backgroundColor: '#FFF1F0',
    color: '#CF1322',
  }),
}));

export const AdjustAmountCard = styled(Box)({
  border: '1px solid #f0f0f0',
  borderRadius: '12px',
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const AdjustAmountInput = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    height: '44px',
    fontSize: '16px',
    fontWeight: 600,
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#E8E8E8',
    },
    '& input': {
      textAlign: 'right',
    },
  },
});

export const AdjustResultBar = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  padding: '10px',
  backgroundColor: '#F0FAF9',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#595959',
});

export const AdjustResultAmount = styled('span')(({ color }) => ({
  fontSize: '16px',
  fontWeight: 700,
  color: color || '#4AB3BC',
}));

export const AdjustMemoCard = styled(Box)({
  border: '1px solid #f0f0f0',
  borderRadius: '12px',
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const AdjustMemoInput = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    fontSize: '14px',
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#E8E8E8',
    },
  },
});

export const AdjustModalFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '12px',
  padding: '0 24px 24px',
});

// ============================================
// Step 3: 명세서
// ============================================
export const PayslipContainer = styled(Box)({
  display: 'flex',
  gap: '20px',
  flex: 1,
});

export const PayslipCard = styled(Box)({
  flex: 1,
  background: '#fff',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0px 0px 8px 0px rgba(0,0,0,0.08)',
});

export const PayslipHeader = styled(Box)({
  background: '#333',
  padding: '28px 32px',
  color: '#fff',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

export const PayslipHeaderLeft = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const PayslipBranchName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#fff',
});

export const PayslipTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 700,
  color: '#fff',
});

export const PayslipEmployeeInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginTop: '4px',
});

export const PayslipEmployeeInfoText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#fff',
});

export const PayslipEmployeeInfoDivider = styled(Box)({
  width: '1px',
  height: '12px',
  backgroundColor: '#a6a6a6',
});

export const PayslipHeaderRight = styled(Box)({
  textAlign: 'right',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  alignSelf: 'stretch',
  gap: '6px',
});

export const PayslipRecipientLabel = styled(Typography)({
  fontSize: '13px',
  color: '#a6a6a6',
});

export const PayslipRecipientName = styled(Typography)({
  fontSize: '22px',
  fontWeight: 700,
  color: '#fff',
});

export const PayslipBody = styled(Box)({
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

// 지급/공제 섹션
export const PayslipSection = styled(Box)({
  border: '1px solid #f2f2f2',
  borderRadius: '8px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const PayslipSectionHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const PayslipSectionTitleBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const PayslipSectionDot = styled(Box)(({ dotColor }) => ({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  backgroundColor: dotColor || '#4AB3BC',
  flexShrink: 0,
}));

export const PayslipSectionTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#333',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const PayslipSectionSubtitle = styled(Typography)({
  fontSize: '12px',
  color: '#a6a6a6',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  paddingLeft: '20px',
});

export const PayslipSectionAmount = styled(Typography)(({ color }) => ({
  fontSize: '18px',
  fontWeight: 700,
  color: color || '#4AB3BC',
}));

export const PayslipDivider = styled(Box)({
  height: '1px',
  backgroundColor: '#f2f2f2',
});

// 항목 2컬럼 그리드
export const PayslipItemsGrid = styled(Box)({
  display: 'flex',
  gap: '12px',
  // 명세서 패널은 좁다(1280px 화면에서 280px 내외). 최소 폭에 못 미치면 접는다.
  flexWrap: 'wrap',
});

// 각 항목 박스
export const PayslipItemBox = styled(Box)({
  // overflow:hidden 이면 자동 최소 크기가 0이 되어 내용보다 작게 찌그러진다.
  // 최소 폭이 없으면 헤더의 제목이 한 글자씩 줄바꿈된다.
  flex: '1 1 200px',
  minWidth: '200px',
  border: '1px solid #f2f2f2',
  borderRadius: '8px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

export const PayslipItemBoxHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 16px',
  height: '32px',
  backgroundColor: '#f4f6f9',
});

export const PayslipItemBoxTitle = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#404040',
});

export const PayslipItemBoxAmount = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#333',
});

export const PayslipItemBoxSubRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 16px',
});

export const PayslipItemBoxSubLabel = styled(Typography)({
  fontSize: '13px',
  color: '#a6a6a6',
  flex: 1,
  marginRight: '8px',
});

export const PayslipItemBoxSubAmount = styled(Typography)({
  fontSize: '13px',
  color: '#737373',
  flexShrink: 0,
});

export const PayslipItemBlock = styled(Box)({
  padding: '12px 16px',
  borderBottom: '1px solid #F0F0F0',
  '&:nth-of-type(odd)': {
    borderRight: '1px solid #F0F0F0',
  },
  '&:last-child, &:nth-last-of-type(2):nth-of-type(odd)': {
    borderBottom: 'none',
  },
});

export const PayslipItemTitle = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#262626',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '4px',
});

export const PayslipItemAmount = styled(Typography)(({ color }) => ({
  fontSize: '14px',
  fontWeight: 700,
  color: color || '#262626',
}));

export const PayslipSubItem = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1px 0 1px 8px',
});

export const PayslipSubLabel = styled(Typography)({
  fontSize: '12px',
  color: '#BFBFBF',
});

export const PayslipSubAmount = styled(Typography)({
  fontSize: '12px',
  color: '#8c8c8c',
});

// 실지급액 박스
export const PayslipTotalBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 16px',
  background: '#F3F9FA',
  borderRadius: '8px',
  border: '1px solid #4AB3BC',
});

export const PayslipTotalLabel = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const PayslipTotalTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#262626',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const PayslipTotalSub = styled(Typography)({
  fontSize: '12px',
  color: '#a6a6a6',
  paddingLeft: '20px',
});

export const PayslipTotalAmount = styled(Typography)({
  fontSize: '32px',
  fontWeight: 700,
  color: '#4AB3BC',
});

export const PayslipFooterText = styled(Typography)({
  textAlign: 'center',
  fontSize: '18px',
  fontWeight: 500,
  color: '#737373',
  padding: '12px 0',
});

// 최근급여 이력 사이드
export const RecentPayCard = styled(Box)({
  width: '280px',
  flexShrink: 0,
  background: '#fff',
  borderRadius: '16px',
  boxShadow: '0px 0px 4px rgba(0,0,0,0.08)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

export const RecentPayHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
});

export const RecentPayTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#404040',
});

export const RecentPayPeriod = styled(Typography)({
  fontSize: '12px',
  color: '#737373',
});

export const RecentPayTableWrapper = styled(Box)({
  border: '1px solid #f2f2f2',
  borderRadius: '8px',
  overflow: 'hidden',
  margin: '0 16px 16px',
});

export const RecentPayTableHead = styled(Box)({
  display: 'flex',
  backgroundColor: '#f4f6f9',
  borderBottom: '1px solid #f2f2f2',
});

export const RecentPayTableHeadCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 16px',
  fontSize: '13px',
  fontWeight: 700,
  color: '#737373',
});

export const RecentPayRow = styled(Box)({
  display: 'flex',
  alignItems: 'stretch',
  borderBottom: '1px solid #f2f2f2',
  '&:last-child': {
    borderBottom: 'none',
  },
});

export const RecentPayMonth = styled(Box)({
  width: '80px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 16px',
  fontSize: '13px',
  fontWeight: 500,
  color: '#404040',
  borderRight: '1px solid #f2f2f2',
});

export const RecentPayAmount = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '10px 16px',
  fontSize: '15px',
  fontWeight: 700,
  color: '#4AB3BC',
});

// 정산완료 확인 모달
export const ConfirmModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '420px',
  backgroundColor: '#fff',
  borderRadius: '16px',
  padding: '40px 32px 28px',
  textAlign: 'center',
  outline: 'none',
});

export const ConfirmModalText = styled(Typography)({
  fontSize: '22px',
  fontWeight: 600,
  color: '#262626',
  lineHeight: 1.6,
});

// 산정 기간 종료 전 정산 시 확인창에 끼워 넣는 안내.
// 확정하면 재계산이 막히므로, 무엇이 반영됐고 무엇이 아직인지 숫자로 보여준다.
export const EarlyNoticeBox = styled(Box)({
  marginTop: '20px',
  padding: '16px 18px',
  backgroundColor: '#FFF7E6',
  border: '1px solid #FFD591',
  borderRadius: '8px',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

export const EarlyNoticeTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#AD6800',
  marginBottom: '2px',
});

export const EarlyNoticeRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: '12px',
  fontSize: '14px',
  color: '#8C6100',
  '& b': { fontWeight: 600, color: '#613400', fontVariantNumeric: 'tabular-nums' },
});

export const EarlyNoticeDesc = styled(Typography)({
  marginTop: '6px',
  paddingTop: '10px',
  borderTop: '1px dashed #FFD591',
  fontSize: '13.5px',
  lineHeight: 1.6,
  color: '#8C6100',
  '& b': { fontWeight: 700, color: '#AD6800' },
});

export const ConfirmModalButtons = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '12px',
  marginTop: '28px',
});
